import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { WebApiContext } from 'ascendon-web-api';
import {
  Box,
  Button,
  Typography,
  Grid,
  Link,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { postMessage } from '../../../utils/postMessage';
import RegistrationModel from '../../model';
import LocaleKeys from '../../../locales/keys';
import { useTranslation, withTranslation } from 'react-i18next';
import compose from 'ramda/src/compose';
import CONSTANTS from '../../../constants/externalReferences';
import getLeadSourceTypeFromConfigParameter from './getLeadSourceTypeFromConfigParameter';
import { getLocalDateFormat, createDateFormat } from '../../../utils/common';
import { payloadTypes } from '../../../constants/metaDataPayloadTypes';
import { getValidCountriesList } from '../../../utils/common';
import { UserSession } from 'ascendon-web-toolkit/utils';
import useCreateSubscriberWithSecurityApi from '../../../common/hooks/useCreateSubscriberWithSecurityApi';
import externalValidationRules from '../../../config/password_config.json';

const useStyles = makeStyles((theme) => ({
  submitButton: {
    display: 'block',
    marginLeft: 'auto',
    paddingLeft: '15px',
    paddingRight: '15px',
    boxShadow: 'none',
    transition: 'none',
    fontWeight: 500,
    fontSize: '13px',
    letterSpacing: '0.2px',
    fontFamily: 'TitilliumWeb-Regular',
    WebkitFontSmoothing: 'auto',
    '&:hover': {
      boxShadow: '0 0 0 2px #ee0000 inset',
      background: '#ffffff',
      color: '#15151e',
    },
  },
  link: {
    marginLeft: '8px',
    fontWeight: 500,
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '17px',
    letterSpacing: '0.2px',
    WebkitFontSmoothing: 'auto',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.primary.main,
    },
  },
  fieldWidth: {
    maxWidth: '270px',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  fieldHeight: {
    height: '54px',
  },
  registerButtonContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  titilliumFont: {
    fontFamily: 'TitilliumWeb-Regular',
    WebkitFontSmoothing: 'auto',
  },
  signIn: {
    display: 'inline',
    fontFamily: 'TitilliumWeb-Regular',
    WebkitFontSmoothing: 'auto',
  },
}));

function RegistrationForm(props) {
  const {
    profileType,
    showAlreadyHaveAccountMessage,
    leadSource,
    securityApiKey,
    securityApiUrl,
    staticApiKey,
    staticApiUrl,
    bypassApigee,
    setIsReady,
  } = props;

  const { t } = useTranslation();
  const [
    requestCreateSubscriberWithSecurityApiUiModel,
    requestCreateSubscriberWithSecurityApiFault,
    requestCreateSubscriberWithSecurityApi,
  ] = useCreateSubscriberWithSecurityApi();
  const [externalData, setExternalData] = useState(null);
  const [createSubscriberError, setCreateSubscriberError] = useState(null);
  const [disableRegisterButton, setDisableRegisterButton] = useState(false);
  const classes = useStyles();
  const WebApi = useContext(WebApiContext);
  const language = useSelector((state) => state.ascendon.settings.language);
  const model = new RegistrationModel(profileType);

  const form = useFormik({
    initialValues: model.getInitialValues(),
    validationSchema: model.getValidationSchema(language, externalData),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    //validateOnMount: true,
    onSubmit: (values) => {
      const currentUrl = window.location.href;

      const leadSourceType = getLeadSourceTypeFromConfigParameter(
        currentUrl,
        externalData,
        leadSource,
      );

      const {
        title,
        firstName,
        lastName,
        dateOfBirth,
        country,
        emailAddress,
        password,
        recieveLatestNews,
      } = values;
      const subscriber = {};
      const credentials = {};
      subscriber.AdditionalProperties = [];

      if (title && title !== 'Select') {
        subscriber.Title = title;
      }
      if (firstName) {
        subscriber.FirstName = firstName;
      }
      if (lastName) {
        subscriber.LastName = lastName;
      }
      if (dateOfBirth) {
        const dateFormat = createDateFormat(getLocalDateFormat());
        const parsedDateformat = dateFormat.parse(dateOfBirth);
        const birthDateTimestamp =
          dateFormat.formatForSubscriberAPI(parsedDateformat);
        subscriber.BirthDate = birthDateTimestamp;
      }
      if (country && country !== 'Select') {
        subscriber.HomeCountry = country;
        subscriber.AdditionalProperties.push({
          ExternalReference: CONSTANTS.MARKETING_COUNTRY,
          Values: [country],
        });
      }
      if (emailAddress) {
        subscriber.Email = emailAddress;
        credentials.Login = emailAddress;
      }
      if (password) {
        credentials.Password = password;
      }

      if (profileType !== 'DATA_CAPTURE') {
        subscriber.AdditionalProperties.push({
          ExternalReference: CONSTANTS.CONSENT_TC,
          Values: ['1'],
        });
      }
      subscriber.AdditionalProperties.push({
        ExternalReference: CONSTANTS.CONSENT_CONTACT,
        Values: [recieveLatestNews ? 1 : 0],
      });
      subscriber.LeadSourceType = leadSourceType;

      //handleSubmit
      if (form.isValid) {
        const postBody = {
          AutoLogin: true,
          Credentials: {
            ...credentials,
          },
          Subscriber: {
            ...subscriber,
          },
        };
        const createSubscriberUrl = '/account/Subscriber/CreateSubscriber';
        const customUrl = `${staticApiUrl}${createSubscriberUrl}`;
        setDisableRegisterButton(true);
        postMessage('module.showLoader');
        requestCreateSubscriberWithSecurityApi({
          subscriberPayload: postBody,
          customUrl,
          securityApiKey,
          securityApiUrl,
          staticApiKey,
          staticApiUrl,
          emulateApigeeCall: bypassApigee,
        });
      }
    },
  });
  const registrationForm = model.createForm(
    form,
    externalData,
    language,
    classes,
  );

  useEffect(() => {
    let isMounted = true;

    const {
      countries,
      titles,
      subscriberRequirements,
      subscriberLeadSourceCode,
      regex,
    } = payloadTypes;
    WebApi.requestAll([
      {
        type: 'code',
        forceRefresh: true,
        payload: [countries],
      },
      {
        type: 'code',
        forceRefresh: true,
        payload: [titles],
      },
      {
        type: 'code',
        forceRefresh: true,
        payload: [subscriberRequirements],
      },
      {
        type: 'code',
        forceRefresh: true,
        payload: [subscriberLeadSourceCode],
      },
      {
        type: 'code',
        forceRefresh: true,
        payload: [regex],
      },
    ])
      .then(() => {
        if (isMounted) {
          setExternalData({
            countryRules: getValidCountriesList(
              WebApi.getMetadataItemByKey('code', countries),
            ),
            titleRules: WebApi.getMetadataItemByKey('code', titles),
            genericRules: WebApi.getMetadataItemByKey(
              'code',
              subscriberRequirements,
            ),
            leadSources: WebApi.getMetadataItemByKey(
              'code',
              subscriberLeadSourceCode,
            ),
            externalValidationRules: externalValidationRules,
            regExRules: WebApi.getMetadataItemByKey('code', regex),
          });

          setIsReady(true);
        }
      })
      .catch((error) => {
        setIsReady(true);
        if (isMounted) {
          console.info(error);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [profileType]);

  const subscribeErrorActivity = (requestFault) => {
    if (requestFault) {
      if (requestFault === 'code_113') {
        form.setFieldError(
          'emailAddress',
          t(LocaleKeys.registration.loginAlreadyExists),
        );
        return;
      }
      setCreateSubscriberError(requestFault);
      postMessage('subscriber.error', {
        error: requestFault,
      });
    }
  };

  useEffect(() => {
    if (requestCreateSubscriberWithSecurityApiFault) {
      setDisableRegisterButton(false);
      subscribeErrorActivity(requestCreateSubscriberWithSecurityApiFault);
      postMessage('module.hideLoader');
    }
  }, [requestCreateSubscriberWithSecurityApiFault]);

  useEffect(() => {
    if (requestCreateSubscriberWithSecurityApiUiModel) {
      postMessage('subscriber.created', {
        ...requestCreateSubscriberWithSecurityApiUiModel,
        subscriptionSource: 'web',
        userRegistrationLevel: 'full',
      });
      UserSession.setSessionData(requestCreateSubscriberWithSecurityApiUiModel);
      postMessage('module.hideLoader');
    }
  }, [requestCreateSubscriberWithSecurityApiUiModel]);

  if (externalData) {
    return (
      <Box mb={10} mt={3}>
        {createSubscriberError ? (
          <Box mb={3}>
            <Typography variant="body2" color="error">
              {createSubscriberError}
            </Typography>
          </Box>
        ) : null}
        <Box mb={9}>
          <Box mb={5}>
            <Typography variant="h2">
              {t(LocaleKeys.registration.createAccount)}
            </Typography>
          </Box>
          <Divider />
        </Box>
        <form>
          {registrationForm}
          <Box component={Grid} mt={6} container>
            <Box
              component={Grid}
              container
              className={classes.registerButtonContainer}
            >
              <Button
                className={classes.submitButton}
                disabled={disableRegisterButton}
                variant="contained"
                color="primary"
                onClick={() => {
                  form.handleSubmit();
                }}
              >
                {t(LocaleKeys.registration.register)}
              </Button>
            </Box>
            <Box component={Grid} mt={9} container>
              <Box component={Grid} item>
                {showAlreadyHaveAccountMessage ? (
                  <>
                    <Typography className={classes.signIn}>
                      {t(LocaleKeys.registration.alreadyHaveAccount)}
                    </Typography>
                    <Link
                      color="textPrimary"
                      className={`${classes.link}`}
                      underline="none"
                      onClick={() => {
                        postMessage('module.loginButton', {});
                      }}
                    >
                      {t(LocaleKeys.signIn.signIn)}
                    </Link>
                  </>
                ) : null}
              </Box>
            </Box>
          </Box>
        </form>
      </Box>
    );
  }

  return (
    <Box mb={10} mt={3}>
      <Box mb={6}>
        <Typography variant="h2">
          {t(LocaleKeys.registration.serviceError)}
        </Typography>
      </Box>
    </Box>
  );
}

export default compose(withTranslation())(RegistrationForm);
